/**
 * @file        src/api/client.js
 * @description Axios instance with silent 401 → /auth/refresh → replay flow.
 *              See CLAUDE_PROJECT_TEMPLATE §3.4. The access token lives in
 *              memory only; the refresh token is the backend's httpOnly cookie.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-06-22 Claude — Initial.
 */
import axios from 'axios';

// Backend mounts every router under /api/v1 (see booking_backend/app.js), so the
// fallback MUST include /v1 — otherwise a production build without
// VITE_API_BASE_URL hits /api/air-search and 404s. Matches hotels.js's default.
const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// The OTP login returns the JWT in the response body (no refresh cookie), so the
// token must be persisted to survive a page refresh — otherwise every authed
// call 401s after reload and the user looks logged in but sees no data. Stored
// in sessionStorage so it lives for the tab session and is cleared on logout.
const TOKEN_KEY = 'nh-access-token';
const readToken = () => {
  try { return sessionStorage.getItem(TOKEN_KEY); } catch { return null; }
};
let accessToken = readToken();
let refreshPromise = null;
let onForceLogout = () => {};

export function setAccessToken(t) {
  accessToken = t || null;
  try {
    if (t) sessionStorage.setItem(TOKEN_KEY, t);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch { /* storage unavailable — keep the in-memory value */ }
}
export function getAccessToken() { return accessToken; }
export function onUnauthorized(handler) { onForceLogout = handler; }

// Which storefront this build is. Sent on every request so the backend applies
// THIS brand's pricing/coupons and records bookings under it. Brand codes are
// 3-char entries in the tenant's `brands` registry (migration 029; 'beyond'
// became 'bey'). This is the Beyond Destination build, so it defaults to
// 'bey'; override via VITE_BRAND for a differently-registered deployment.
const BRAND = import.meta.env.VITE_BRAND || 'bey';

export const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { 'X-Brand': BRAND },
});

api.interceptors.request.use((config) => {
  if (accessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

async function refreshOnce() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${BASE_URL}/auth/refresh`, null, { withCredentials: true })
      .then((res) => {
        accessToken = res.data?.data?.accessToken || null;
        return accessToken;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err.response?.status;
    const original = err.config || {};
    const url = original.url || '';

    // Don't try to refresh on the refresh / login endpoint itself.
    const isAuthCall =
      url.includes('/auth/refresh') ||
      url.includes('/auth/login') ||
      url.includes('/AuthenticateLogin') ||
      url.includes('/generateloginotp');

    if (status === 401 && !isAuthCall && !original._retried) {
      original._retried = true;
      try {
        const fresh = await refreshOnce();
        if (fresh) {
          original.headers = { ...(original.headers || {}), Authorization: `Bearer ${fresh}` };
          return api.request(original);
        }
      } catch (_) {
        // fall through to forced logout below
      }
      accessToken = null;
      onForceLogout();
    }

    return Promise.reject(err);
  },
);

/** Unwrap the { data, meta } success shape. */
export async function get(url, config)  { const r = await api.get(url, config);  return r.data; }
export async function post(url, body, config) { const r = await api.post(url, body, config); return r.data; }
export async function patch(url, body, config){ const r = await api.patch(url, body, config); return r.data; }
export async function put(url, body, config)  { const r = await api.put(url, body, config); return r.data; }
export async function del(url, config)  { const r = await api.delete(url, config); return r.data; }
